import React, { useCallback, useMemo, useState } from "react";
import Button from "components/common/Button/Button";
import DataTable from "components/common/DataTable/DataTable";
import { ArchiveBook, DocumentDownload } from "iconsax-react";
import { toast } from "react-toastify";
import { usePaymentHistoryDetail } from "../../hooks/usePaymentHistoryDetail";
import { downloadPaymentHistoryCSV } from "../../api/downloadPaymentHistoryCSV";
import {
  getCurrentYear,
  generateYearRange,
  formatPaymentDate,
} from "../../helpers/utils";
import UpcomingSubscription from "./UpcomingSubscription";
import TransactionDetailsModal from "./TransactionDetailsModal";
import styles from "./PaymentHistoryView.module.css";
import LoadingLoader from "components/LoadingLoader";

const STATUS_CLASS_MAP = {
  Paid: styles.statusPaid,
  Unpaid: styles.statusUnpaid,
  "Waived/Comp": styles.statusWaived,
  Frozen1: styles.statusFrozen,
  Frozen2: styles.statusFrozen,
  "Deactivated/Unsubscribed": styles.statusDeactivated,
  Refunded: styles.statusRefunded,
};

const PaymentHistoryView = ({
  companyId,
  basePlan,
  isWaived,
  enabled = true,
}) => {
  const years = useMemo(() => generateYearRange(), []);
  const [filters, setFilters] = useState({ year: getCurrentYear().toString() });
  const { data, isLoading } = usePaymentHistoryDetail(
    companyId,
    filters,
    enabled
  );
  const [downloading, setDownloading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const records = useMemo(() => data?.result?.records || [], [data]);
  const hasRecords = records.length > 0;

  const handleDownloadCSV = useCallback(async () => {
    try {
      setDownloading(true);
      await downloadPaymentHistoryCSV(companyId, filters);
      toast.success("Payment history downloaded successfully");
    } catch (error) {
      toast.error("Failed to download payment history");
    } finally {
      setDownloading(false);
    }
  }, [companyId, filters]);

  const handleYearChange = useCallback((e) => {
    const { value } = e.target;
    setFilters((prev) => ({ ...prev, year: value }));
  }, []);

  const getStatusClass = useCallback(
    (status) => `${styles.statusBadge} ${STATUS_CLASS_MAP[status] || ""}`,
    []
  );

  const handleInvoiceClick = useCallback((invoiceUrl) => {
    invoiceUrl
      ? window.open(invoiceUrl, "_blank")
      : toast.error("No Invoice Available");
  }, []);

  const handleOpenDetails = useCallback((record) => {
    setSelectedPayment(record);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedPayment(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: ({ row }) => row.index + 1,
        size: 50,
      },
      {
        accessorKey: "paymentStatus",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          return <span className={getStatusClass(status)}>{status}</span>;
        },
      },
      {
        accessorKey: "paymentType",
        header: "Type",
      },
      {
        accessorKey: "paymentPlanUser",
        header: "Users",
      },
      {
        accessorKey: "paymentAmount",
        header: "Amount",
        cell: ({ getValue }) => `$${Number(getValue() || 0).toFixed(2)}`,
      },
      {
        accessorKey: "dateOfPayment",
        header: "Date",
        cell: ({ getValue }) => formatPaymentDate(getValue()),
      },
      {
        accessorKey: "invoice_url",
        header: "View Bill",
        // Show details for EVERY row (including Unpaid/Failed/Frozen) so an admin
        // can inspect failed payments — not just successful/refunded ones.
        cell: ({ row }) => (
          <div
            className={styles.archiveButton}
            style={{ cursor: "pointer" }}
            onClick={() => handleOpenDetails(row.original)}
            title="View transaction details"
          >
            <ArchiveBook size="32" color="#0d6efd" />
          </div>
        ),
      },
    ],
    [getStatusClass, handleOpenDetails]
  );

  const filtersBar = (
    <div className={styles.filters}>
      <select
        value={filters.year}
        onChange={handleYearChange}
        className={styles.yearSelect}
      >
        <option value="">All Years</option>
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
      <Button
        variant="primary"
        onClick={handleDownloadCSV}
        disabled={downloading || !hasRecords}
      >
        <DocumentDownload size="20" className={styles.iconMargin} />
        {downloading ? "Downloading..." : "Download CSV"}
      </Button>
    </div>
  );

  if (isLoading) return <LoadingLoader />;

  return (
    <div className={styles.container}>
      <UpcomingSubscription basePlan={basePlan} isWaived={isWaived} />
      <div className={styles.header}>
        <h5 className={styles.title}>Payment History</h5>
        {filtersBar}
      </div>

      {!hasRecords ? (
        <div className={styles.emptyState}>
          <img
            className={styles.emptyImage}
            src="/no-subs.png"
            alt="No payment history"
          />
          <h5>Currently there is no payment history.</h5>
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={records}
            pageSize={10}
            emptyMessage="No payment history available"
          />
          <TransactionDetailsModal
            show={Boolean(selectedPayment)}
            payment={selectedPayment}
            onClose={handleCloseDetails}
            onDownloadReceipt={handleInvoiceClick}
            getStatusClass={getStatusClass}
            companyId={companyId}
          />
        </>
      )}
    </div>
  );
};

export default PaymentHistoryView;
